<template>
    <PageWrapper>
        <template #headline>
            <fa icon="fa-store" />
            {{ platform.platform_name }}
        </template>

        <template #actions>
            <BackButton
                v-if="$router.options.history.state.back"
                @click="router.go(-1)"
            />
            <BackButton
                v-if="
                    platform.platform_name &&
                    decodeURIComponent(
                        $router.options.history.state.back?.toString() as string
                    ) !== `/platforms/${platform.platform_name}`
                "
                icon="fa-eye"
                title="platform"
                @click="goToDetails"
            />
            <BackButton
                v-if="$router.options.history.state.back !== '/platforms'"
                icon="fa-list"
                title="platforms"
                @click="toPlatforms"
            />
            <SaveButton @click="savePlatform" />
        </template>

        <AccordionBasic
            title="platform_details"
            class="mb-2"
            :open="true"
        >
            <TextField
                v-model="platform.platform_name"
                name="platform_name"
                label="platform_name"
                help="help_platform_name"
                :required="true"
            />
            <SelectField
                v-model="platform.parent_platform_name"
                v-model:options="parentPlatformOptions"
                name="parent_platform_name"
                label="parent_platform_name"
                help="help_parent_platform_name"
                :on-search="loadParentPlatformOptions"
            />
            <TextField
                v-model="platform.platform_url"
                name="url"
                label="url"
                help="help_platform_url"
                :required="true"
            />
            <ImageUpload
                v-model="platform.platform_logo"
                name="platform_logo"
                label="platform_logo"
                help="help_platform_logo"
                type="platform"
            />
        </AccordionBasic>

        <AccordionBasic
            title="platform_settings"
            help="help_platform_settings"
            class="mb-2"
        >
            <ToggleSwitch
                v-model="platform.acceptance_document_active"
                name="acceptance_document_active"
                label="acceptance_document_active"
                help="help_acceptance_document_active"
            />

            <ToggleSwitch
                v-model="platform.membership_management_active"
                name="membership_management_active"
                label="membership_management_active"
                help="help_membership_management_active"
            />

            <ToggleSwitch
                v-model="platform.certificate_active"
                name="order_certificate_active"
                label="order_certificate_active"
                help="help_order_certificate_active"
            />

            <ToggleSwitch
                v-model="platform.zoho_sync_enabled"
                name="zoho_sync_enabled"
                label="zoho_sync_enabled"
                help="help_zoho_sync_enabled"
            />

            <ToggleSwitch
                v-model="platform.order_transfer_enabled"
                name="transfer_investment"
                label="transfer_investment"
                help="help_transfer_investment"
            />
            <ToggleSwitch
                v-if="platform.has_credential_voucherify"
                v-model="platform.voucherify_enabled"
                name="voucherify_enabled"
                label="voucherify_enabled"
                help="help_voucherify_enabled"
            />
            <ToggleSwitch
                v-model="platform.agio_disagio_feature_active"
                name="agio_disagio_feature_active"
                label="agio_disagio_feature_active"
                help="help_agio_disagio_feature_active"
            />
            <ToggleSwitch
                v-model="platform.webhooks_activated"
                name="webhooks_activated"
                label="webhooks_activated"
                help="help_webhooks_activated"
            />
        </AccordionBasic>

        <AccordionBasic
            title="links"
            class="mb-2"
        >
            <TextField
                v-model="platform.terms_url"
                name="terms_url"
                label="terms"
                help="help_platform_terms"
                :required="true"
            />
            <TextField
                v-model="platform.privacy_url"
                name="privacy_url"
                label="privacy_url"
                help="help_platform_privacy_url"
                :required="true"
            />
        </AccordionBasic>
        <AccordionBasic
            title="email_settings"
            class="mb-2"
        >
            <TextField
                v-model="platform.admin_email"
                name="admin_email"
                label="admin_email"
                help="help_platform_admin_email"
            />
            <SelectField
                v-model="platform.mail_template"
                name="mail_template"
                label="mail_template"
                help="help_mail_template"
                :options="[
                    {
                        label: $t('mail_type_standard'),
                        value: 'standard',
                    },
                ]"
            />
            <ColorPickerField
                v-if="platform.mail_template === 'standard'"
                v-model="platform.mail_template_color"
                name="mail_template_color"
                label="mail_template_color"
                help="help_mail_template_color"
            />
            <ColorPickerField
                v-if="platform.mail_template === 'standard'"
                v-model="platform.mail_template_action_button_color"
                name="mail_template_action_button_color"
                label="mail_template_action_button_color"
                help="help_mail_template_action_button_color"
            />
            <ColorPickerField
                v-if="platform.mail_template === 'standard'"
                v-model="platform.mail_template_action_button_text_color"
                name="mail_template_action_button_text_color"
                label="mail_template_action_button_text_color"
                help="help_mail_template_action_button_text_color"
            />
        </AccordionBasic>
        <AccordionBasic
            title="clearing_systems"
            class="mb-2"
        >
            <h3 class="mb-1 block text-sm font-medium">
                {{ $t('clearing_systems') }}
                <span class="text-danger-500">*</span>
            </h3>
            <ToggleSwitch
                v-model="clearingSystemCi"
                name="ci"
                label="clearing_system_ci"
            />
            <ToggleSwitch
                v-model="clearingSystemCf"
                name="cf"
                label="clearing_system_cf"
            />
            <ToggleSwitch
                v-model="clearingSystemEmission"
                name="emission"
                label="clearing_system_emission"
            />

            <div class="mt-2 text-xs">{{ $t('help_clearing_systems') }}</div>
        </AccordionBasic>
        <AccordionBasic
            title="order_status_for_total_funding_sum"
            class="mb-2"
        >
            <h3 class="mb-1 block text-sm font-medium">
                {{ $t('order_status_for_total_funding_sum') }}
                <span class="text-danger-500">*</span>
            </h3>
            <ToggleSwitch
                v-model="orderStatusPending"
                name="pending"
                label="pending"
            />
            <ToggleSwitch
                v-model="orderStatusPaid"
                name="paid"
                label="paid"
            />
            <ToggleSwitch
                v-model="orderStatusAccepted"
                name="accepted"
                label="accepted"
            />

            <div class="mt-2 text-xs">
                {{ $t('help_order_status_for_total_funding_sum') }}
            </div>
        </AccordionBasic>
        <AccordionBasic
            title="smtp_settings"
            class="mb-2"
        >
            <TextField
                v-model="platform.mail_host"
                name="mail_host"
                label="mail_host"
                help="help_mail_host"
                :required="true"
            />
            <NumberField
                v-model="platform.mail_port"
                name="mail_port"
                label="mail_port"
                help="help_mail_port"
                :required="true"
            />
            <SelectField
                v-model="platform.mail_encryption"
                name="mail_encryption"
                label="mail_encryption"
                help="help_mail_encryption"
                :options="[
                    {
                        label: $t('none'),
                        value: null,
                    },
                    {
                        label: 'TLS',
                        value: 'tls',
                    },
                    {
                        label: 'SSL',
                        value: 'ssl',
                    },
                ]"
            />
            <TextField
                v-model="platform.mail_from_address"
                name="mail_from_address"
                label="mail_from_address"
                help="help_mail_from_address"
                :required="true"
            />
            <TextField
                v-model="platform.mail_from_name"
                name="mail_form_name"
                label="mail_form_name"
                help="help_mail_form_name"
                :required="true"
            />
            <TextField
                v-model="platform.mail_username"
                name="mail_username"
                label="mail_username"
                help="help_mail_username"
            />
            <TextField
                v-model="platform.mail_password"
                name="mail_password"
                label="mail_password"
                help="help_mail_password"
            />
        </AccordionBasic>
        <AccordionBasic
            v-if="platform.voucherify_enabled"
            title="voucherify_settings"
            class="mb-2"
        >
            <TextField
                v-model="platform.voucher_wallet"
                name="voucher_wallet"
                label="voucher_wallet"
                help="help_voucher_wallet"
            />
            <h3 class="mb-2 font-bold">{{ $t('voucher_campaigns') }}</h3>
            <div
                v-for="(campaign, index) in platform.voucherify_campaigns"
                :key="index"
                class="flex flex-row gap-4"
            >
                <TextField
                    v-model="campaign.voucherify_campaign_name"
                    name="voucher_campaign_name"
                    label="voucher_campaign_name"
                    help="help_voucher_campaign_name"
                    :disabled="campaign.created_at !== null"
                />
                <SelectField
                    v-model="campaign.type"
                    name="voucher_campaign_type"
                    label="voucher_campaign_type"
                    help="help_voucher_campaign_type"
                    :options="mapEnumToArray(VoucherCampaignType)"
                    :disabled="true"
                />
                <div class="my-4">
                    <ToggleSwitch
                        v-model="campaign.default"
                        class="mt-8"
                        :name="'voucher_campaign_default-' + index"
                        label="voucher_campaign_default"
                        help="help_voucher_campaign_default"
                        @toggle="() => toggleVoucherCampaignDefault(index)"
                    />
                </div>
                <div class="mt-10">
                    <DeleteButton
                        v-if="!campaign.default"
                        @click="deleteVoucherifyCampaign(campaign)"
                    />
                </div>
            </div>
            <button
                class="btn-sm bg-primary-additional-500 hover:bg-primary-additional-600 text-white"
                @click.stop="addVoucherCampaign"
            >
                {{ $t('add_voucher_campaign') }}
            </button>
        </AccordionBasic>
        <AccordionBasic
            title="active_emails"
            class="mb-2"
        >
            <div
                v-for="template_name in PlatformEmailTemplates"
                :key="template_name"
                class="border-b border-gray-200 py-4 last:border-0"
            >
                <ToggleSwitch
                    :id="`help_${template_name}`"
                    v-model="platform[template_name]"
                    :name="template_name"
                    :label="template_name"
                    :help="`help_${template_name}`"
                >
                </ToggleSwitch>
                <div
                    v-if="
                        schedulePropertyExists(template_name, platform) &&
                        platform[template_name] === true &&
                        showDurationPicker
                    "
                    class="intervals mt-1 inline-block rounded border-2 p-2 py-2 shadow"
                >
                    <div
                        v-for="(val, index) in platform[
                            convertToScheduleProperty(template_name)
                        ]"
                        :key="index"
                    >
                        <DurationPicker
                            v-model="
                                platform[
                                    convertToScheduleProperty(template_name)
                                ][index]
                            "
                            class="inline-block"
                        />
                        <DeleteButton
                            class="ml-1"
                            @click="
                                deleteScheduleInterval(
                                    index,
                                    convertToScheduleProperty(template_name),
                                    platform
                                )
                            "
                        />
                    </div>

                    <button
                        class="btn-sm bg-primary-additional-500 hover:bg-primary-additional-600 mt-2 w-full p-2 text-white"
                        @click="
                            addNewValueForScheduleProperty(
                                platform,
                                convertToScheduleProperty(template_name)
                            )
                        "
                    >
                        <fa icon="plus" />
                        <span class="ml-2">{{ $t('add') }}</span>
                    </button>
                </div>
            </div>
        </AccordionBasic>
        <AccordionBasic
            title="qr_code_type"
            class="mb-2"
        >
            <SelectField
                v-model="platform.payment_qr_code_type"
                name="payment_qr_code_type"
                label="payment_qr_code_type"
                help="help_payment_qr_code_type"
                :options="mapEnumToArray(QRCodeType)"
                title="payment_qr_code_type"
                class="mb-2"
                :required="true"
            />
        </AccordionBasic>
        <AccordionBasic
            title="kyc_settings"
            class="mb-2"
        >
            <SelectField
                v-model="platform.kyc_field_restriction_mode"
                name="kyc_field_restriction_mode"
                :placeholder="$t('kyc_field_restriction_mode_no_restriction')"
                label="kyc_field_restriction_mode"
                help="help_kyc_field_restriction_mode"
                :options="mapEnumToArray(KycFieldRestrictionMode)"
                title="kyc_kyc_field_restriction_mode"
                class="mb-6"
            />
            <ToggleSwitch
                v-model="platform.require_kyc_id_for_investments"
                name="require_kyc_id_for_investments"
                label="require_kyc_id_for_investments"
                help="help_require_kyc_id_for_investments"
            />
            <ToggleSwitch
                v-model="platform.require_bank_account_for_investments"
                name="require_bank_account_for_investments"
                label="require_bank_account_for_investments"
                help="help_require_bank_account_for_investments"
            />
            <ToggleSwitch
                v-model="platform.require_kyc_address_for_investments"
                name="require_kyc_address_for_investments"
                label="require_kyc_address_for_investments"
                help="help_require_kyc_address_for_investments"
            />
            <ToggleSwitch
                v-model="platform.require_kyc_tax_for_investments"
                name="require_kyc_tax_for_investments"
                label="require_kyc_tax_for_investments"
                help="help_require_kyc_tax_for_investments"
            />
            <NumberField
                v-model="platform.kyc_reset_threshold"
                name="kyc_reset_threshold"
                label="kyc_reset_threshold"
                help="help_kyc_reset_threshold"
            />
            <ToggleSwitch
                v-if="userStore.can('set_kyc_provider')"
                v-model="platform.external_kyc_active"
                name="external_kyc_active"
                label="external_kyc_active"
                help="help_external_kyc_active"
            />
            <SelectField
                v-if="
                    userStore.can('set_kyc_provider') &&
                    platform.external_kyc_active
                "
                v-model="platform.kyc_provider"
                name="kyc_provider"
                label="kyc_provider"
                help="help_kyc_provider"
                :options="mapEnumToArray(KycProvider)"
                title="kyc_provider"
                class="mb-2"
            />
            <template v-if="platform.kyc_provider === KycProvider.IDNOW">
                <TextField
                    v-model="platform.external_kyc_api_host"
                    name="external_kyc_api_host"
                    label="api_host"
                    help="help_api_host"
                    :required="true"
                />
                <TextField
                    v-model="platform.external_kyc_redirect_host"
                    name="external_kyc_redirect_host"
                    label="redirect_host"
                    help="help_redirect_host"
                    :required="true"
                />
                <TextField
                    v-model="platform.external_kyc_customer"
                    name="external_kyc_customer"
                    label="customer"
                    help="help_customer"
                    :required="true"
                />
                <TextField
                    v-model="platform.external_kyc_api_key"
                    name="external_kyc_api_key"
                    label="api_key"
                    help="help_api_key"
                    :required="true"
                />
            </template>
            <template v-if="platform.kyc_provider === KycProvider.SIGNTEQ">
                <ToggleSwitch
                    v-model="platform.signteq_kyc_qes"
                    name="signteq_kyc_qes"
                    label="signteq_kyc_qes"
                    help="help_signteq_kyc_qes"
                />
                <TextField
                    v-model="platform.signteq_api_host"
                    name="signteq_api_host"
                    label="api_host"
                    help="help_api_host"
                    :required="true"
                />
                <TextField
                    v-model="platform.signteq_redirect_host"
                    name="signteq_redirect_host"
                    label="redirect_host"
                    help="help_redirect_host"
                    :required="true"
                />
                <TextField
                    v-model="platform.signteq_customer"
                    name="signteq_customer"
                    label="customer"
                    help="help_customer"
                    :required="true"
                />
                <TextField
                    v-model="platform.signteq_api_key"
                    name="signteq_api_key"
                    label="api_key"
                    help="help_api_key"
                    :required="true"
                />
                <TextField
                    v-model="platform.signteq_signature_api_key"
                    help="help_signature_api_key"
                    label="signature_api_key"
                    name="signteq_signature_api_key"
                />
                <TextField
                    v-model="platform.signteq_signature_organization_id"
                    name="platform_signteq_signature_organization_id"
                    label="platform_signteq_signature_organization_id"
                    help="help_platform_signteq_signature_organization_id"
                />
            </template>
        </AccordionBasic>
        <AccordionBasic
            title="kyb_settings"
            class="mb-2"
        >
            <div
                class="flex h-auto w-full flex-col items-start justify-start space-y-2"
            >
                <p class="text-base font-bold">
                    {{ $t('company_account_creation_mail_receipents') }}
                </p>
                <textarea
                    v-model="legalKycCreateAccountRecipients"
                    class="h-48 w-1/2 rounded-md border border-gray-300 p-2"
                ></textarea>
                <p class="text-sm text-gray-500">
                    !!! {{ $t('help_email_receipents') }}
                </p>
            </div>
            <br />
            <div
                class="flex h-auto w-full flex-col items-start justify-start space-y-2"
            >
                <p class="text-base font-bold">
                    {{
                        $t(
                            'company_account_document_submission_mail_receipents'
                        )
                    }}
                </p>
                <textarea
                    v-model="legalKycDocumentSubmitRecipients"
                    class="h-48 w-1/2 rounded-md border border-gray-300 p-2"
                ></textarea>
                <p class="text-sm text-gray-500">
                    !!! {{ $t('help_email_receipents') }}
                </p>
            </div>
        </AccordionBasic>
        <PlatformInvestorProfileForm
            :settings="platform.investor_profile_field_settings"
            :parent-platform-name="platform.parent_platform_name"
            :compliance-profile="platform.compliance_profile"
            :show-wallet-menu-item="platform.show_wallet_menu_item"
            :show-news-menu-item="platform.show_news_menu_item"
            @update:settings="
                (newSettings) =>
                    (platform.investor_profile_field_settings = newSettings)
            "
            @update:compliance-profile="
                (value) => (platform.compliance_profile = value)
            "
            @update:show-wallet-menu-item="
                (value) => (platform.show_wallet_menu_item = value)
            "
            @update:show-news-menu-item="
                (value) => (platform.show_news_menu_item = value)
            "
        />
        <AccordionBasic
            title="cancel_investment_policy"
            class="mb-2"
        >
            <div
                class="flex h-auto w-full flex-col items-start justify-start space-y-4"
            >
                <ToggleSwitch
                    v-model="platform.cancel_order_button_enabled"
                    name="cancel_order_button_enabled"
                    label="cancel_order_button_enabled"
                    help="help_cancel_order_button_enabled"
                />
                <template v-if="platform.cancel_order_button_enabled">
                    <SelectField
                        v-model="platform.cancel_counter_start"
                        name="cancel_counter_start"
                        label="cancel_counter_start"
                        help="help_cancel_counter_start"
                        :options="[
                            {
                                label: $t('cancel_counter_start_created_at'),
                                value: CancelCounterStart.CREATED_AT,
                            },
                            {
                                label: $t('cancel_counter_start_paid_at'),
                                value: CancelCounterStart.PAID_AT,
                            },
                            {
                                label: $t('cancel_counter_start_accepted_at'),
                                value: CancelCounterStart.ACCEPTED_AT,
                            },
                        ]"
                    />
                    <NumberField
                        v-model="platform.cancel_period_days"
                        name="cancel_period_days"
                        label="cancel_period_days"
                        help="help_cancel_period_days"
                        :min="1"
                    />
                    <TextField
                        v-model="platform.cancel_contact_email"
                        name="cancel_contact_email"
                        label="cancel_contact_email"
                        help="help_cancel_contact_email"
                    />
                    <div class="w-full">
                        <p class="text-base font-bold">
                            {{ $t('cancel_notification_recipients') }}
                        </p>
                        <textarea
                            v-model="cancelNotificationRecipientsText"
                            class="mt-1 h-32 w-1/2 rounded-md border border-gray-300 p-2"
                        ></textarea>
                        <p class="text-sm text-gray-500">
                            {{ $t('help_email_receipents') }}
                        </p>
                    </div>
                    <ToggleSwitch
                        v-model="platform.acceptance_after_cancel_period"
                        name="acceptance_after_cancel_period"
                        label="acceptance_after_cancel_period"
                        help="help_acceptance_after_cancel_period"
                    />
                    <div
                        v-if="
                            platform.acceptance_after_cancel_period &&
                            platform.cancel_counter_start ===
                                CancelCounterStart.ACCEPTED_AT
                        "
                        class="w-full rounded-md border border-yellow-400 bg-yellow-50 p-3 text-sm text-yellow-800"
                    >
                        {{
                            $t(
                                'warning_acceptance_after_cancel_period_accepted_at_invalid'
                            )
                        }}
                    </div>
                    <ToggleSwitch
                        v-model="platform.payout_through_acceptance"
                        name="payout_through_acceptance"
                        label="payout_through_acceptance"
                        help="help_payout_through_acceptance"
                    />
                    <ToggleSwitch
                        v-if="isSecupayPlatform"
                        v-model="platform.cancel_auto_refund_enabled"
                        name="cancel_auto_refund_enabled"
                        label="cancel_auto_refund_enabled"
                        help="help_cancel_auto_refund_enabled"
                    />
                </template>
            </div>
        </AccordionBasic>
        <AccordionBasic
            v-if="platform.esac_kyc_active"
            title="esac_kyc"
            class="mb-2"
        >
            <div>
                <FileUploadButton
                    title="esac_form_natural_person"
                    accept=".pdf"
                    :file-name="platform.esac_form_natural_person.name"
                    :url="platform.esac_form_natural_person.thumb_image_url"
                    @submit="onEsacFormNaturalPersonUpload"
                    @delete="onEsacFormNaturalPersonDelete"
                />
            </div>
            <div class="mt-4">
                <FileUploadButton
                    title="esac_form_legal_entity"
                    accept=".pdf"
                    :file-name="platform.esac_form_legal_entity.name"
                    :url="platform.esac_form_legal_entity.thumb_image_url"
                    @submit="onEsacFormLegalEntityUpload"
                    @delete="onEsacFormLegalEntityDelete"
                />
            </div>
            <div class="mt-4">
                <FileUploadButton
                    title="esac_form_legal_ubo"
                    accept=".pdf"
                    :file-name="platform.esac_form_legal_ubo.name"
                    :url="platform.esac_form_legal_ubo.thumb_image_url"
                    @submit="onEsacFormLegalUboUpload"
                    @delete="onEsacFormLegalUboDelete"
                />
            </div>
        </AccordionBasic>
        <AccordionBasic
            v-if="platform.membership_management_active"
            title="membership_management"
            help="help_membership_management"
        >
            <ToggleSwitch
                v-model="platform.gift_membership"
                name="gift_membership"
                label="gift_membership"
                :disabled="!platform.membership_management_active"
                help="help_gift_membership"
                class="my-5"
            />

            <TextField
                v-if="platform.gift_membership"
                v-model="platform.gift_membership_faq"
                :disabled="
                    !platform.membership_management_active ||
                    !platform.gift_membership
                "
                name="gift_membership_faq"
                :rows="3"
                label="gift_membership_faq"
                help="help_gift_membership_faq"
            />

            <TextField
                v-if="platform.gift_membership"
                v-model="platform.gift_membership_faq_url"
                name="gift_membership_faq_url"
                label="gift_membership_faq_url"
                help="help_gift_membership_faq_url"
                :disabled="
                    !platform.membership_management_active ||
                    !platform.gift_membership
                "
            />

            <TextField
                v-model="platform.requested_membership_documents_email"
                name="membership_documents_admin_email"
                label="membership_documents_admin_email"
                help="help_requested_membership_documents_email"
                :disabled="!platform.membership_management_active"
            />

            <ToggleSwitch
                v-model="platform.membership_type_manual"
                name="membership_type_manual"
                label="membership_type_manual"
                :disabled="!platform.membership_management_active"
                class="mt-10"
            />
            <ToggleSwitch
                v-model="platform.membership_type_sproof"
                name="membership_type_sproof"
                label="membership_type_sproof"
                :disabled="true"
            />

            <ToggleSwitch
                v-if="platform.membership_type_sproof"
                v-model="platform.qualified_signature"
                name="qualified_signature"
                label="qualified_signature"
                help="help_qualified_signature"
                :disabled="!platform.membership_management_active"
            />

            <ToggleSwitch
                v-model="platform.use_rule"
                name="use_rule"
                label="use_rule"
                help="help_use_rule"
                :disabled="!platform.membership_management_active"
                class="mt-10"
            />

            <ToggleSwitch
                v-model="platform.membership_number_active"
                name="membership_number_active"
                label="membership_number_active"
                help="help_membership_number_active"
                :disabled="!platform.membership_management_active"
            />

            <TextField
                v-if="platform.membership_number_active"
                v-model="platform.membership_number_prefix_natural"
                name="membership_number_prefix_natural"
                label="membership_number_prefix_natural"
                help="help_membership_number_prefix_natural"
            />
            <TextField
                v-if="platform.membership_number_active"
                v-model="platform.membership_number_prefix_legal"
                name="membership_number_prefix_legal"
                label="membership_number_prefix_legal"
                help="help_membership_number_prefix_legal"
            />
        </AccordionBasic>
        <AccordionBasic
            v-if="platform.zoho_sync_enabled"
            title="zoho_integration"
        >
            <div class="mt-4">
                <div
                    v-if="platform.zoho_sync_inherited"
                    class="text-sm text-gray-500"
                >
                    <span
                        >{{ $t('inherited_from_parent') }}.
                        {{ $t('zoho_override_prompt') }}</span
                    >
                    <div class="mt-2 flex gap-2">
                        <button
                            type="button"
                            class="text-xs font-medium text-blue-600 hover:underline"
                            @click="overrideZohoInherited = true"
                        >
                            {{ $t('yes') }}
                        </button>
                        <button
                            type="button"
                            class="text-xs font-medium text-gray-500 hover:underline"
                            @click="overrideZohoInherited = false"
                        >
                            {{ $t('no') }}
                        </button>
                    </div>
                </div>
                <template
                    v-if="
                        !platform.zoho_sync_inherited || overrideZohoInherited
                    "
                >
                    <TextField
                        v-model="platform.zoho_environment"
                        name="zoho_environment"
                        label="Zoho Environment"
                    />
                    <TextField
                        v-model="platform.zoho_webhook_url"
                        name="zoho_webhook_url"
                        label="Zoho Webhook URL"
                    />
                    <TextField
                        v-model="platform.zoho_creator_owner"
                        name="zoho_creator_owner"
                        label="Zoho Creator Owner"
                        required
                    />
                    <TextField
                        v-model="platform.zoho_creator_app_name"
                        name="zoho_creator_app_name"
                        label="Zoho Creator App Name"
                        required
                    />
                    <TextField
                        v-model="platform.zoho_creator_server"
                        name="zoho_creator_server"
                        label="Zoho Creator Server"
                        required
                    />
                    <TextField
                        v-model="platform.zoho_client_id"
                        name="zoho_client_id"
                        label="Zoho Client ID"
                        required
                    />
                    <TextField
                        v-model="platform.zoho_client_secret"
                        name="zoho_client_secret"
                        label="Zoho Client Secret"
                        required
                    />
                    <TextField
                        v-model="platform.zoho_refresh_token"
                        name="zoho_refresh_token"
                        label="Zoho Refresh Token"
                        required
                    />
                    <TextField
                        v-model="platform.zoho_redirect_uri"
                        name="zoho_redirect_uri"
                        label="Zoho Redirect URI"
                    />
                    <TextField
                        v-model="platform.zoho_api_domain"
                        name="zoho_api_domain"
                        label="Zoho API Domain"
                    />
                </template>
            </div>
        </AccordionBasic>
        <AccordionBasic title="workdrive_integration">
            <div class="mt-4">
                <TextField
                    v-model="platform.workdrive_client_id"
                    name="workdrive_client_id"
                    label="workdrive_client_id"
                />
                <TextField
                    v-model="platform.workdrive_client_secret"
                    name="workdrive_client_secret"
                    label="workdrive_client_secret"
                />
                <TextField
                    v-model="platform.workdrive_refresh_token"
                    name="workdrive_refresh_token"
                    label="workdrive_refresh_token"
                />
                <TextField
                    v-model="platform.workdrive_api_domain"
                    name="workdrive_api_domain"
                    label="workdrive_api_domain"
                    placeholder="workdrive.zoho.eu"
                />
                <TextField
                    v-model="platform.workdrive_team_folder_id"
                    name="workdrive_team_folder_id"
                    label="workdrive_team_folder_id"
                />
            </div>
        </AccordionBasic>
        <PlatformInvestmentDeskSettingsForm
            :settings="platform.investment_desk_settings"
            @update:settings="
                (newSettings) =>
                    (platform.investment_desk_settings = newSettings)
            "
        />
        <AccordionBasic title="payment_account">
            <div class="mt-4">
                <SelectField
                    v-model="platform.payment_account_id"
                    name="payment_account_id"
                    :placeholder="$t('payment_account')"
                    label="payment_account"
                    help="help_payment_account"
                    :options="
                        paymentAccountOptions.map((paymentAccountOption) => ({
                            value: paymentAccountOption.id,
                            label: paymentAccountOption.name,
                        }))
                    "
                    title="payment_account_id"
                    class="mb-6"
                />
            </div>
        </AccordionBasic>
        <AccordionBasic
            v-if="platform.redirect_to_another_platform"
            title="redirection_settings"
        >
            <div class="mt-4">
                <TextField
                    v-model="platform.redirection_url"
                    name="redirection_url"
                    label="redirection_url"
                    help="help_redirection_url"
                    :required="true"
                />
                <ToggleSwitch
                    v-model="platform.redirect_on_login"
                    name="redirect_on_login"
                    label="redirect_on_login"
                    help="help_redirect_on_login"
                />
                <ToggleSwitch
                    v-model="platform.redirect_on_register"
                    name="redirect_on_register"
                    label="redirect_on_register"
                    help="help_redirect_on_register"
                />
            </div>
        </AccordionBasic>
        <AccordionBasic title="sm_marketplace">
            <div class="flex items-center justify-start space-x-4">
                <ToggleSwitch
                    v-model="platform.sm_marketplace"
                    name="sm_marketplace"
                    label="sm_marketplace"
                    help="help_sm_marketplace"
                    class="my-5"
                />
            </div>

            <div class="flex items-center justify-start space-x-4">
                <ToggleSwitch
                    v-model="platform.sm_trading_enabled"
                    name="sm_trading_enabled"
                    label="sm_trading_enabled"
                    help="help_sm_trading_enabled"
                    class="my-5"
                />
            </div>

            <div class="flex items-center justify-start space-x-4">
                <ToggleSwitch
                    v-model="platform.sm_dashboard_marketplace_button_enabled"
                    name="sm_dashboard_marketplace_button_enabled"
                    label="sm_dashboard_marketplace_button_enabled"
                    help="help_sm_dashboard_marketplace_button_enabled"
                    class="my-5"
                />
            </div>

            <div class="flex items-center justify-start space-x-4">
                <ToggleSwitch
                    v-model="
                        platform.sm_dashboard_back_to_platform_button_enabled
                    "
                    name="sm_dashboard_back_to_platform_button_enabled"
                    label="sm_dashboard_back_to_platform_button_enabled"
                    help="help_sm_dashboard_back_to_platform_button_enabled"
                    class="my-5"
                />
            </div>

            <div class="flex items-center justify-start space-x-4">
                <ToggleSwitch
                    v-model="platform.sm_marketplace_listings_visible"
                    name="sm_marketplace_listings_visible"
                    label="sm_marketplace_listings_visible"
                    help="help_sm_marketplace_listings_visible"
                    class="my-5"
                />
            </div>

            <div class="grid grid-cols-1 gap-6 md:grid-cols-2">
                <TextField
                    v-model="platform.sm_frontend_url"
                    name="sm_frontend_url"
                    label="sm_frontend_url"
                    help="help_sm_frontend_url"
                    :required="true"
                />
            </div>

            <hr class="border-gray-200" />

            <div class="grid grid-cols-1 gap-6 md:grid-cols-3">
                <NumberField
                    v-model="platform.sm_signing_deadline"
                    name="sm_signing_deadline"
                    label="sm_signing_deadline"
                    help="help_sm_signing_deadline"
                />
                <NumberField
                    v-model="platform.sm_payment_fulfillment_deadline"
                    name="sm_payment_fulfillment_deadline"
                    label="sm_payment_fulfillment_deadline"
                    help="help_sm_payment_fulfillment_deadline"
                />
                <NumberField
                    v-model="platform.sm_payment_confirmation_deadline"
                    name="sm_payment_confirmation_deadline"
                    label="Zahlungsbestätigung Frist (Tage)"
                    help="Frist in Tagen bis zur Eskalation"
                />
            </div>
            <hr class="border-gray-200" />
            <div class="grid grid-cols-1 gap-6 md:grid-cols-2">
                <TextField
                    v-model="stringEscalationEmails"
                    name="sm_escalation_emails"
                    label="Eskalations-E-Mails"
                    help="Mehrere E-Mails mit Komma trennen (z.B. support@oekostrom.at, admin@oekostrom.at)"
                />
            </div>
            <div class="mt-4">
                <label class="mb-2 block text-sm font-medium">
                    {{ $t('sm_required_base_data_fields') }}
                </label>
                <div class="flex flex-col space-y-2">
                    <ToggleSwitch
                        v-for="field in availableBaseDataFields"
                        :key="field"
                        :model-value="isFieldSelected(field)"
                        :name="`account_${field}`"
                        :label="`account_${field}`"
                        @update:model-value="
                            (val) => updateRequiredField(field, val)
                        "
                    />
                </div>
            </div>
        </AccordionBasic>
        <AccordionBasic title="user_feedback">
            <SelectField
                v-model="platform.user_feedback_provider"
                name="user_feedback_provider"
                :placeholder="$t('user_feedback_provider_none')"
                label="user_feedback_provider"
                help="help_user_feedback_provider"
                :options="mapEnumToArray(UserFeedbackProvider)"
                title="user_feedback_provider"
                class="mb-2"
            />
            <template
                v-if="
                    platform.user_feedback_provider ===
                    UserFeedbackProvider.PUSHFEEDBACK
                "
            >
                <TextField
                    v-model="platform.user_feedback_pushfeedback_project"
                    name="user_feedback_pushfeedback_project"
                    label="user_feedback_pushfeedback_project"
                    help="help_user_feedback_pushfeedback_project"
                    :required="true"
                />
                <TextField
                    v-model="platform.user_feedback_pushfeedback_api_key"
                    name="user_feedback_pushfeedback_api_key"
                    label="user_feedback_pushfeedback_api_key"
                    help="help_user_feedback_pushfeedback_api_key"
                />
            </template>
            <template
                v-if="
                    platform.user_feedback_provider ===
                    UserFeedbackProvider.USERSNAP
                "
            >
                <TextField
                    v-model="platform.user_feedback_usersnap_project_code"
                    name="user_feedback_usersnap_project_code"
                    label="user_feedback_usersnap_project_code"
                    help="help_user_feedback_usersnap_project_code"
                    :required="true"
                />
            </template>
        </AccordionBasic>
    </PageWrapper>
</template>
<script setup lang="ts">
import DurationPicker from '@components/DurationPicker.vue'
import { useEnum } from '@composables/common'
import BackButton from '@partials/actions/BackButton.vue'
import DeleteButton from '@partials/actions/DeleteButton.vue'
import FileUploadButton from '@partials/actions/FileUploadButton.vue'
import {
    AccordionBasic,
    ColorPickerField,
    ImageUpload,
    NumberField,
    SelectField,
    TextField,
    ToggleSwitch,
} from '@src/components'
import useEditLock from '@src/composables/editLock'
import { QRCodeType, VoucherCampaignType } from '@src/enums'
import { PaymentAccountType } from '@src/enums/PaymentAccount'
import {
    CancelCounterStart,
    KycFieldRestrictionMode,
    KycProvider,
    PlatformEmailTemplates,
    UserFeedbackProvider,
} from '@src/enums/Platform'
import SaveButton from '@src/partials/actions/SaveButton.vue'
import PageWrapper from '@src/partials/pages/PageWrapper.vue'
import { Platform, VoucherCampaign } from '@src/types'
import { PaymentAccount } from '@src/types/PaymentAccount'
import { getPaymentAccounts } from '@src/utils/api/paymentAccounts'
import {
    getPlatform,
    getPlatforms,
    updatePlatform,
} from '@src/utils/api/platforms'
import { UploadFileResponse } from '@src/utils/api/s3'
import { useUserStore } from '@src/utils/stores/user'
import { useToastStore } from '@stores/toast'
import { Ref, computed, nextTick, onMounted, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRoute, useRouter } from 'vue-router'
import PlatformInvestmentDeskSettingsForm from './PlatformInvestmentDeskSettingsForm.vue'
import PlatformInvestorProfileForm from './PlatformInvestorProfileForm.vue'

const { mapEnumToArray } = useEnum()

const router = useRouter()
const route = useRoute()
const toast = useToastStore()
const i18n = useI18n()
const userStore = useUserStore()
const showDurationPicker = ref(true)

const stringEscalationEmails = ref('')
const stringRequiredBaseDataFields = ref('')

const legalKycCreateAccountRecipients = ref<string>('')
const legalKycDocumentSubmitRecipients = ref<string>('')
const cancelNotificationRecipientsText = ref<string>('')
const overrideZohoInherited = ref<boolean>(false)

useEditLock('platform', route.params.slug as string)

async function savePlatform() {
    platform.value.funding_total_status = orderStatusSelection.value
    platform.value.corporate_kyc_support_recipients_registration =
        legalKycCreateAccountRecipients.value
            .split('\n')
            .map((recipient) => recipient.trim())
            .filter(Boolean)
    platform.value.corporate_kyc_support_recipients_document_submit =
        legalKycDocumentSubmitRecipients.value
            .split('\n')
            .map((recipient) => recipient.trim())
            .filter(Boolean)
    platform.value.cancel_notification_recipients =
        cancelNotificationRecipientsText.value
            .split('\n')
            .map((recipient) => recipient.trim())
            .filter(Boolean)
    if (!platform.value.cancel_order_button_enabled) {
        platform.value.cancel_period_days = null
        platform.value.cancel_contact_email = null
    }
    if (platform.value.zoho_sync_inherited && !overrideZohoInherited.value) {
        platform.value.zoho_sync_enabled = false
    }
    overrideZohoInherited.value = false

    platform.value.sm_escalation_emails = stringEscalationEmails.value
        ? stringEscalationEmails.value
              .split(',')
              .map((e) => e.trim())
              .filter(Boolean)
        : []

    clearAndFormatMailSchedules(platform.value)
    await updatePlatform(platform.value)
    toast.success(
        i18n.t('toast_success_platform_saved', {
            platform: platform.value.platform_name,
        })
    )
    if (platform.value.platform_logo) {
        platform.value.platform_logo.upload_key = null
    }
    loadPlatform()
}

function goToDetails(): void {
    router.push({
        name: 'platform',
        params: { slug: platform.value.platform_name },
    })
}

function toPlatforms() {
    router.push({ name: 'platforms' })
}

const platform = ref<Platform>(new Platform())
const parentPlatformOptions = ref<string[]>([])

const paymentAccountOptions = ref<PaymentAccount[]>([])

async function loadParentPlatformOptions(search: string) {
    const initialPlatformsRequest = await getPlatforms({
        'filter[platform_name]': search,
        'filter[is_not_child]': true,
    })
    parentPlatformOptions.value = initialPlatformsRequest.data.platforms.map(
        (platform: Platform) => {
            return platform.platform_name as string
        }
    )
}

async function loadPaymentAccountOptions() {
    const paymentAccountsRequest = await getPaymentAccounts()
    paymentAccountOptions.value = paymentAccountsRequest.data.payment_accounts
}

// clearing systems
// FIXME: this is a bit hacky, but it works for now. We should probably refactor this to use the computed() function as with the orderStatusSelection
function handleCS(cs: string, value: boolean): void {
    if (value === true) {
        if (
            !platform.value.clearing_systems.find(
                (item) => item.clearing_system === cs
            )
        ) {
            platform.value.clearing_systems.push({ clearing_system: cs })
        }
    }
    if (value === false) {
        const clearingSystem = platform.value.clearing_systems.find(
            (item) => item.clearing_system === cs
        )
        if (clearingSystem) {
            platform.value.clearing_systems.splice(
                platform.value.clearing_systems.indexOf(clearingSystem),
                1
            )
        }
    }
}
const clearingSystemCi: Ref<boolean> = ref<boolean>(false)
watch(clearingSystemCi, (value) => handleCS('CI', value))
const clearingSystemCf: Ref<boolean> = ref<boolean>(false)
watch(clearingSystemCf, (value) => handleCS('CF', value))
const clearingSystemEmission: Ref<boolean> = ref<boolean>(false)
watch(clearingSystemEmission, (value) => handleCS('EMISSION', value))

// order status for funding sum
const orderStatusPending: Ref<boolean> = ref<boolean>(false)
const orderStatusPaid: Ref<boolean> = ref<boolean>(false)
const orderStatusAccepted: Ref<boolean> = ref<boolean>(false)
const isSecupayPlatform = computed(
    () => platform.value.payment_account?.type === PaymentAccountType.SECUPAY
)

const orderStatusSelection = computed((): number[] => {
    const orderStatus = []
    if (orderStatusPending.value) {
        orderStatus.push(0)
    }
    if (orderStatusPaid.value) {
        orderStatus.push(1)
    }
    if (orderStatusAccepted.value) {
        orderStatus.push(2)
    }
    return orderStatus
})

watch(
    () => platform.value.effective_funding_total_status,
    (statuses) => {
        orderStatusPending.value = statuses?.includes(0) ?? false
        orderStatusPaid.value = statuses?.includes(1) ?? false
        orderStatusAccepted.value = statuses?.includes(2) ?? false
    }
)

watch(
    () => platform.value,
    (newPlatform) => {
        if (newPlatform) {
            stringEscalationEmails.value = newPlatform.sm_escalation_emails
                ? newPlatform.sm_escalation_emails.join(', ')
                : ''

            stringRequiredBaseDataFields.value =
                newPlatform.sm_required_base_data_fields
                    ? newPlatform.sm_required_base_data_fields.join(', ')
                    : ''
        }
    },
    { immediate: true }
)

function addVoucherCampaign() {
    const voucherCampaign = new VoucherCampaign()
    if (!platform.value.voucherify_campaigns.length) {
        voucherCampaign.default = true
    }
    platform.value.voucherify_campaigns.push(voucherCampaign)
}

function toggleVoucherCampaignDefault(index: number) {
    platform.value.voucherify_campaigns.forEach((campaign, i) => {
        if (i === index) {
            campaign.default = true
        } else {
            campaign.default = false
        }
    })
}

async function loadPlatform() {
    const platformRequest = await getPlatform(route.params.slug as string)
    platform.value = platformRequest.data.platform

    if (platform.value.parent_platform_name) {
        await loadParentPlatformOptions(platform.value.parent_platform_name)
    }

    await loadPaymentAccountOptions()
}

function onEsacFormNaturalPersonUpload(
    response: UploadFileResponse,
    fileName: string
) {
    platform.value.esac_form_natural_person.name = fileName
    platform.value.esac_form_natural_person.upload_key = response.key
    platform.value.esac_form_natural_person.thumb_image_url =
        response.temp_storage_url
}

function onEsacFormNaturalPersonDelete() {
    platform.value.esac_form_natural_person = {
        delete: true,
    }
}

function onEsacFormLegalEntityUpload(
    response: UploadFileResponse,
    fileName: string
) {
    platform.value.esac_form_legal_entity.name = fileName
    platform.value.esac_form_legal_entity.upload_key = response.key
    platform.value.esac_form_legal_entity.thumb_image_url =
        response.temp_storage_url
}

function onEsacFormLegalEntityDelete() {
    platform.value.esac_form_legal_entity = {
        delete: true,
    }
}

function onEsacFormLegalUboUpload(
    response: UploadFileResponse,
    fileName: string
) {
    platform.value.esac_form_legal_ubo.name = fileName
    platform.value.esac_form_legal_ubo.upload_key = response.key
    platform.value.esac_form_legal_ubo.thumb_image_url =
        response.temp_storage_url
}

function onEsacFormLegalUboDelete() {
    platform.value.esac_form_legal_ubo = {
        delete: true,
    }
}

function onAcceptanceDocumentUpload(
    response: UploadFileResponse,
    fileName: string
) {
    platform.value.acceptance_document = {
        name: fileName,
        upload_key: response.key,
        thumb_image_url: response.temp_storage_url,
    }
}

function onAcceptanceDocumentDelete() {
    platform.value.acceptance_document = {
        delete: true,
    }
}

function onMembershipDocumentUpload(
    response: UploadFileResponse,
    fileName: string
) {
    platform.value.membership_document = {
        name: fileName,
        upload_key: response.key,
        thumb_image_url: response.temp_storage_url,
    }
}

function onMembershipDocumentDelete() {
    platform.value.membership_document = {
        delete: true,
    }
}

function deleteScheduleInterval(
    indexToDelete: number,
    prop: string,
    platform: Platform
) {
    showDurationPicker.value = false
    platform[prop] = platform[prop].filter(
        (item: number, index: number) => indexToDelete !== index
    )
    nextTick().then(() => {
        showDurationPicker.value = true
    })
}

function clearAndFormatMailSchedules(platform: Platform) {
    PlatformEmailTemplates.forEach((item) => {
        const scheduled_prop = convertToScheduleProperty(item)
        if (schedulePropertyExists(item, platform)) {
            if (platform[scheduled_prop] === null) {
                platform[scheduled_prop] = []
            }
            platform[scheduled_prop] = platform[scheduled_prop].filter(
                (item) => item > 0
            )
            //platform[scheduled_prop] = JSON.stringify(platform[scheduled_prop])
        }
    })
}

function addNewValueForScheduleProperty(
    platform: Platform,
    prop: string
): void {
    if (platform[prop] === null) {
        platform[prop] = []
    }
    platform[prop].push(0)
}

function convertToScheduleProperty(property: string): string {
    return property.replace('mail_active_', 'mail_schedule_')
}

function schedulePropertyExists(template: string, platform: object): boolean {
    const schedule_prop = convertToScheduleProperty(template)
    return Object.keys(platform).includes(schedule_prop)
}

function deleteVoucherifyCampaign(voucherifyCampaign: VoucherCampaign) {
    const index = platform.value.voucherify_campaigns.indexOf(
        voucherifyCampaign,
        0
    )
    if (index > -1) {
        platform.value.voucherify_campaigns.splice(index, 1)
    }
}

watch(
    () => platform.value.external_kyc_active,
    (value) => {
        if (!value) {
            platform.value.kyc_provider = null
        }
    }
)

const availableBaseDataFields = ref<string[]>([])

watch(
    () => platform.value.sm_required_base_data_field_options,
    (options) => {
        if (options && options.length) {
            availableBaseDataFields.value = options
        }
    },
    { immediate: true, deep: true }
)

function isFieldSelected(field: string): boolean {
    if (!platform.value.sm_required_base_data_fields) {
        return false
    }
    return platform.value.sm_required_base_data_fields.includes(field)
}

function updateRequiredField(field: string, isChecked: boolean) {
    if (!Array.isArray(platform.value.sm_required_base_data_fields)) {
        platform.value.sm_required_base_data_fields = []
    }

    const currentArray = [...platform.value.sm_required_base_data_fields]

    if (isChecked) {
        if (!currentArray.includes(field)) {
            currentArray.push(field)
        }
    } else {
        const index = currentArray.indexOf(field)
        if (index > -1) {
            currentArray.splice(index, 1)
        }
    }

    platform.value.sm_required_base_data_fields = currentArray
}

onMounted(async () => {
    if (route.params.slug) {
        await loadPlatform()
        legalKycCreateAccountRecipients.value =
            platform.value.corporate_kyc_support_recipients_registration.join(
                '\n'
            )
        legalKycDocumentSubmitRecipients.value =
            platform.value.corporate_kyc_support_recipients_document_submit.join(
                '\n'
            )
        cancelNotificationRecipientsText.value = (
            platform.value.cancel_notification_recipients ?? []
        ).join('\n')
        clearingSystemCi.value = platform.value.clearing_systems.find(
            (cs) => cs.clearing_system === 'CI'
        )
            ? true
            : false
        clearingSystemCf.value = platform.value.clearing_systems.find(
            (cs) => cs.clearing_system === 'CF'
        )
            ? true
            : false
        clearingSystemEmission.value = platform.value.clearing_systems.find(
            (cs) => cs.clearing_system === 'EMISSION'
        )
            ? true
            : false
    }
})
</script>
